/*
  Hatch storefront behaviour. Vanilla, deferred, no dependencies. One delegated
  listener per event type on document, so re-rendered sections keep working.
  Each block below is labelled with its milestone; quick order is in hx-quick-order.js.
*/
(() => {
  'use strict';

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Scrolls el into view inside box only. scrollIntoView also moves the page, which yanks
  // it back to a sticky bar's resting place when box sits inside one.
  const reveal = (box, el) => {
    const b = box.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.top < b.top) box.scrollTop -= b.top - r.top;
    else if (r.bottom > b.bottom) box.scrollTop += r.bottom - b.bottom;
    if (r.left < b.left) box.scrollLeft -= b.left - r.left;
    else if (r.right > b.right) box.scrollLeft += r.right - b.right;
  };

  /* ---------------------------------------------------------------- */
  /* Accordion                                                         */
  /* <button class="hx-acc__btn" data-hx-acc aria-expanded="false"     */
  /*         aria-controls="panel-id">                                 */
  /* <div id="panel-id" class="hx-acc__panel" hidden>                  */
  /* Wrap in [data-hx-acc-single] to allow one open item at a time.    */
  /* ---------------------------------------------------------------- */

  const setAccordion = (btn, open) => {
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    btn.setAttribute('aria-expanded', String(open));
    if (panel) panel.hidden = !open;
  };

  const toggleAccordion = (btn) => {
    const open = btn.getAttribute('aria-expanded') !== 'true';
    const group = btn.closest('[data-hx-acc-single]');
    if (open && group) {
      group.querySelectorAll('[data-hx-acc][aria-expanded="true"]').forEach((other) => {
        if (other !== btn) setAccordion(other, false);
      });
    }
    setAccordion(btn, open);
  };

  /* ---------------------------------------------------------------- */
  /* Tabs                                                              */
  /* <div role="tablist" data-hx-tabs>                                 */
  /*   <button role="tab" aria-selected aria-controls="panel-id">      */
  /* <div role="tabpanel" id="panel-id" hidden>                        */
  /* ---------------------------------------------------------------- */

  const tabsOf = (list) => Array.from(list.querySelectorAll('[role="tab"]'));

  const selectTab = (tab, focus) => {
    const list = tab.closest('[data-hx-tabs]');
    if (!list) return;
    tabsOf(list).forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', String(selected));
      t.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    if (focus) tab.focus();
  };

  const onTabKey = (event, tab) => {
    const tabs = tabsOf(tab.closest('[data-hx-tabs]'));
    const i = tabs.indexOf(tab);
    const map = {
      ArrowRight: (i + 1) % tabs.length,
      ArrowLeft: (i - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    };
    if (!(event.key in map)) return;
    event.preventDefault();
    selectTab(tabs[map[event.key]], true);
  };

  /* ---------------------------------------------------------------- */
  /* Quantity stepper                                                  */
  /* <div class="hx-qty" data-hx-qty>                                  */
  /*   <button class="hx-qty__btn" data-hx-step="-1" aria-label>       */
  /*   <input class="hx-qty__input" type="number" min max step>        */
  /*   <button class="hx-qty__btn" data-hx-step="1" aria-label>        */
  /* Fires a bubbling change event on the input.                       */
  /* ---------------------------------------------------------------- */

  const qtyBounds = (input) => ({
    min: input.min !== '' ? Number(input.min) : 0,
    max: input.max !== '' ? Number(input.max) : Infinity,
    step: Number(input.step) || 1,
  });

  const syncQty = (wrap) => {
    const input = wrap.querySelector('input');
    if (!input) return;
    const { min, max } = qtyBounds(input);
    const value = Number(input.value) || 0;
    wrap.querySelectorAll('[data-hx-step]').forEach((btn) => {
      const dir = Number(btn.dataset.hxStep);
      btn.disabled = dir < 0 ? value <= min : value >= max;
    });
  };

  const stepQty = (btn) => {
    const wrap = btn.closest('[data-hx-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const { min, max, step } = qtyBounds(input);
    const current = Number(input.value) || 0;
    const next = Math.min(max, Math.max(min, current + Number(btn.dataset.hxStep) * step));
    if (next === current) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncQty(wrap);
  };

  /* ---------------------------------------------------------------- */
  /* Scroller arrows                                                   */
  /* <button class="hx-iconbtn" data-hx-scroll="prev|next"             */
  /*         aria-controls="scroller-id" aria-label>                   */
  /* <div class="hx-scroller" id="scroller-id">                        */
  /* ---------------------------------------------------------------- */

  const syncScroller = (scroller) => {
    const max = scroller.scrollWidth - scroller.clientWidth - 2;
    const atStart = scroller.scrollLeft <= 2;
    const atEnd = scroller.scrollLeft >= max;
    scroller.toggleAttribute('data-hx-at-end', atEnd);
    document.querySelectorAll(`[data-hx-scroll][aria-controls="${scroller.id}"]`).forEach((btn) => {
      btn.disabled = btn.dataset.hxScroll === 'prev' ? atStart : atEnd;
      // nothing to scroll: hide the arrow pair
      const pair = btn.closest('.hx-arrows');
      if (pair) pair.hidden = atStart && atEnd;
    });
  };

  const scrollBy = (btn) => {
    const scroller = document.getElementById(btn.getAttribute('aria-controls'));
    if (!scroller) return;
    const dir = btn.dataset.hxScroll === 'prev' ? -1 : 1;
    scroller.scrollBy({ left: dir * scroller.clientWidth * 0.9, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  /* ---------------------------------------------------------------- */
  /* Delegation                                                        */
  /* ---------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;

    const acc = t.closest('[data-hx-acc]');
    if (acc) return toggleAccordion(acc);

    const tab = t.closest('[data-hx-tabs] [role="tab"]');
    if (tab) return selectTab(tab, false);

    const step = t.closest('[data-hx-step]');
    if (step) return stepQty(step);

    const scroll = t.closest('[data-hx-scroll]');
    if (scroll) return scrollBy(scroll);
  });

  document.addEventListener('keydown', (event) => {
    const t = event.target;
    if (t instanceof Element && t.matches('[data-hx-tabs] [role="tab"]')) onTabKey(event, t);
  });

  // Typed quantities: clamp and refresh the button states
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const wrap = input.closest('[data-hx-qty]');
    if (!wrap) return;
    const { min, max } = qtyBounds(input);
    const value = Number(input.value);
    if (Number.isNaN(value) || value < min) input.value = min;
    else if (value > max) input.value = max;
    syncQty(wrap);
  });

  /* Snap rows (phone carousels): the dot of the card nearest the start  */
  /* is on; at the end of the row, the last one. With                   */
  /* data-hx-snap="pages" there is one dot per screenful instead, made  */
  /* here, for rows that show several small items at once.              */
  /* <div data-hx-snap><ul data-hx-snap-track>…</ul>                     */
  /* <div data-hx-snap-dots><span>…</div></div>                          */
  const syncSnap = (snap) => {
    const row = snap && snap.querySelector('[data-hx-snap-track]');
    const box = snap && snap.querySelector('[data-hx-snap-dots]');
    if (!row || !box || !row.firstElementChild) return;
    const max = row.scrollWidth - row.clientWidth - 2;
    if (snap.dataset.hxSnap === 'pages') {
      const pages = max > 0 ? 1 + Math.ceil(max / row.clientWidth) : 1;
      if (box.children.length !== pages) box.innerHTML = '<span class="hx-snapdots__dot"></span>'.repeat(pages);
      box.style.display = pages < 2 ? 'none' : '';
      const page = max > 0 ? Math.round((Math.min(row.scrollLeft, max) / max) * (pages - 1)) : 0;
      Array.from(box.children).forEach((dot, k) => dot.classList.toggle('is-on', k === page));
      return;
    }
    const dots = box.querySelectorAll(':scope > span');
    if (!dots.length) return;
    const items = Array.from(row.children);
    let on = 0;
    if (max > 0 && row.scrollLeft >= max) {
      on = items.length - 1;
    } else {
      let best = Infinity;
      items.forEach((item, k) => {
        const gap = Math.abs(item.offsetLeft - row.firstElementChild.offsetLeft - row.scrollLeft);
        if (gap < best) { best = gap; on = k; }
      });
    }
    dots.forEach((dot, k) => dot.classList.toggle('is-on', k === on));
  };

  let snapResize = 0;
  window.addEventListener('resize', () => {
    clearTimeout(snapResize);
    snapResize = setTimeout(() => document.querySelectorAll('[data-hx-snap]').forEach(syncSnap), 150);
  }, { passive: true });

  // scroll does not bubble, so listen in the capture phase
  let scrollFrame = 0;
  document.addEventListener(
    'scroll',
    (event) => {
      const el = event.target;
      if (!(el instanceof Element)) return;
      // A snap row has a handful of cards, so its dots update straight away
      if (el.matches('[data-hx-snap-track]')) {
        syncSnap(el.closest('[data-hx-snap]'));
        return;
      }
      if (!el.matches('.hx-scroller[id]')) return;
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => syncScroller(el));
    },
    { capture: true, passive: true }
  );


  /* ---------------------------------------------------------------- */
  /* M2: mega menu (lg and up)                                         */
  /* Opens on hover with a 180ms close delay, on click or tap, and on  */
  /* keyboard. Escape closes and returns focus to the trigger.         */
  /* ---------------------------------------------------------------- */

  const desktop = () => window.matchMedia('(min-width: 1024px)').matches;
  let megaTimer = 0;
  let megaHoverAt = 0; // a click right after a hover-open must not close it again

  const megaTriggers = (header) => Array.from(header.querySelectorAll('[data-hx-mega-trigger]'));

  const closeMega = (header, focusTrigger) => {
    megaTriggers(header).forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      if (wasOpen && focusTrigger) trigger.focus();
    });
  };

  const openMega = (header, panelId) => {
    clearTimeout(megaTimer);
    megaTriggers(header).forEach((trigger) => {
      const match = trigger.getAttribute('aria-controls') === panelId;
      trigger.setAttribute('aria-expanded', String(match));
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.hidden = !match;
    });
  };

  const scheduleClose = (header) => {
    clearTimeout(megaTimer);
    megaTimer = setTimeout(() => closeMega(header, false), 180);
  };

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !desktop()) return;
    const t = event.target instanceof Element ? event.target : null;
    const header = t && t.closest('[data-hx-header]');
    if (!header) return;
    const trigger = t.closest('[data-hx-mega-trigger]');
    if (trigger) {
      if (trigger.getAttribute('aria-expanded') !== 'true') megaHoverAt = Date.now();
      return openMega(header, trigger.getAttribute('aria-controls'));
    }
    if (t.closest('[data-hx-mega]')) return clearTimeout(megaTimer);
    if (t.closest('.hx-nav__item, .hx-nav__quick a')) scheduleClose(header);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || !desktop()) return;
    const header = event.target instanceof Element && event.target.closest('[data-hx-header]');
    if (!header) return;
    const to = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (!to || !to.closest('[data-hx-mega], [data-hx-mega-trigger]')) scheduleClose(header);
  });

  /* ---------------------------------------------------------------- */
  /* M2: mobile and tablet drawer                                      */
  /* ---------------------------------------------------------------- */

  let drawerReturn = null;

  const drawerLevel = (drawer, subId) => {
    const list = drawer.querySelector('[data-hx-drawer-level="1"]');
    const back = drawer.querySelector('[data-hx-drawer-back]');
    const title = drawer.querySelector('[data-hx-drawer-title]');
    drawer.querySelectorAll('[data-hx-drawer-level="2"]').forEach((sub) => { sub.hidden = sub.id !== subId; });
    list.hidden = Boolean(subId);
    back.hidden = !subId;
    if (!title.dataset.base) title.dataset.base = title.textContent;
    const trigger = subId && drawer.querySelector(`[data-hx-drawer-sub="${subId}"]`);
    title.textContent = trigger ? trigger.dataset.title : title.dataset.base;
  };

  const openDrawer = (button) => {
    const drawer = document.getElementById(button.getAttribute('aria-controls'));
    if (!drawer) return;
    drawerReturn = button;
    drawerLevel(drawer, null);
    drawer.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('hx-lock');
    drawer.querySelector('[role="dialog"]').focus();
  };

  const closeDrawer = (drawer) => {
    drawer.hidden = true;
    document.documentElement.classList.remove('hx-lock');
    if (drawerReturn) {
      drawerReturn.setAttribute('aria-expanded', 'false');
      drawerReturn.focus();
      drawerReturn = null;
    }
  };

  const trapFocus = (event, container) => {
    const focusable = Array.from(container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------------------------------------------------------------- */
  /* M2: predictive search, /search/suggest.json                       */
  /* ---------------------------------------------------------------- */

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const money = (amount) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(Number(amount));
    } catch (e) {
      return `$${Number(amount).toFixed(2)}`;
    }
  };

  let suggestTimer = 0;
  let suggestController = null;

  const suggestParts = (input) => {
    const form = input.closest('[data-hx-search]');
    return { form, list: form && form.querySelector('[data-hx-suggest]') };
  };

  const closeSuggest = (input) => {
    const { list } = suggestParts(input);
    if (!list) return;
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  };

  const renderSuggest = (input, products) => {
    const { list } = suggestParts(input);
    if (!list) return;
    products = products.filter((p) => !(list.dataset.hide || '').includes(`|${p.handle}|`));
    if (!products.length) {
      list.innerHTML = `<p class="hx-suggest__empty">${escapeHtml(list.dataset.empty || '')}</p>`;
    } else {
      list.innerHTML = products.map((p, i) => {
        const img = p.featured_image && p.featured_image.url
          ? `<img src="${escapeHtml(p.featured_image.url)}&width=96" alt="" width="48" height="48" loading="lazy">`
          : '<span></span>';
        return `<a class="hx-suggest__item" role="option" id="${list.id}-${i}" aria-selected="false" href="${escapeHtml(p.url)}">${img}`
          + `<span><span class="hx-suggest__title">${escapeHtml(p.title)}</span><br><span class="hx-suggest__meta">${escapeHtml(p.vendor || '')}</span></span>`
          + `<span class="hx-suggest__price">${p.price ? escapeHtml(money(p.price)) : ''}</span></a>`;
      }).join('');
    }
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const fetchSuggest = (input) => {
    const q = input.value.trim();
    if (q.length < 2) return closeSuggest(input);
    if (suggestController) suggestController.abort();
    suggestController = new AbortController();
    const url = `${root()}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
    fetch(url, { signal: suggestController.signal })
      .then((r) => r.json())
      .then((data) => renderSuggest(input, (data.resources && data.resources.results && data.resources.results.products) || []))
      .catch(() => {});
  };

  const moveSuggest = (input, dir) => {
    const { list } = suggestParts(input);
    if (!list || list.hidden) return false;
    const items = Array.from(list.querySelectorAll('[role="option"]'));
    if (!items.length) return false;
    let i = items.findIndex((el) => el.getAttribute('aria-selected') === 'true');
    i = (i + dir + items.length) % items.length;
    items.forEach((el, n) => el.setAttribute('aria-selected', String(n === i)));
    input.setAttribute('aria-activedescendant', items[i].id);
    reveal(list, items[i]);
    return true;
  };

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('[data-hx-search]')) return;
    clearTimeout(suggestTimer);
    suggestTimer = setTimeout(() => fetchSuggest(input), 200);
  });

  /* ---------------------------------------------------------------- */
  /* M2: cart count and Dawn cart drawer                               */
  /* Hatch.addToCart(items) posts to /cart/add.js, then opens Dawn's   */
  /* drawer with the re-rendered sections, or goes to the cart page.   */
  /* ---------------------------------------------------------------- */

  const setCount = (count) => {
    document.querySelectorAll('[data-hx-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
      el.classList.remove('is-bump');
      void el.offsetWidth;
      if (count > 0) el.classList.add('is-bump');
    });
  };

  const refreshCount = () => fetch(`${root()}cart.js`).then((r) => r.json()).then((cart) => setCount(cart.item_count)).catch(() => {});

  /* ---------------------------------------------------------------- */
  /* Dangerous goods surcharge. snippets/hx-dgs-state says whether the */
  /* cart needs the surcharge line; syncDgs adds or removes it after   */
  /* every cart change, so the cart holds exactly one while a marked   */
  /* product is in it and none otherwise.                              */
  /* ---------------------------------------------------------------- */

  // Dawn's pub/sub (pubsub.js, constants.js). PUB_SUB_EVENTS is a top-level const there, a
  // global binding but not a window property, so it is read by name.
  const cartEvents = () => (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function' ? PUB_SUB_EVENTS : null);
  const publishCart = (data) => {
    if (cartEvents()) publish(cartEvents().cartUpdate, { source: 'hatch', cartData: data });
  };

  const DGS_SECTION = 'hx-dgs';

  const readDgs = (source) => {
    const doc = typeof source === 'string' ? new DOMParser().parseFromString(source, 'text/html') : source;
    const el = doc && doc.querySelector('script[data-hx-dgs]');
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  };

  // The request that brings the cart in line with the state, or null when it already is
  const dgsRequest = (state) => {
    if (!state || !state.variant) return null;
    const keys = Object.keys(state.lines || {});
    if (state.need && !keys.length) return { url: 'cart/add.js', body: { items: [{ id: state.variant, quantity: 1 }] } };
    const updates = {};
    let changed = false;
    keys.forEach((key, i) => {
      const want = state.need && i === 0 ? 1 : 0;
      updates[key] = want;
      if (state.lines[key] !== want) changed = true;
    });
    return changed ? { url: 'cart/update.js', body: { updates } } : null;
  };

  // Dawn's drawer, once cart-drawer.js (loaded after this file) has defined it
  const getDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    return drawer && typeof drawer.getSectionsToRender === 'function' ? drawer : null;
  };

  const cartPageSection = () => {
    const cart = document.querySelector('[data-hx-cart]');
    return cart ? cart.closest('.shopify-section').id.replace('shopify-section-', '') : '';
  };

  // Dawn's drawer and the cart page, re-rendered from a response that asked for them
  const renderCart = (data, openDrawer) => {
    if (!data || !data.sections) return;
    const drawer = getDrawer();
    if (drawer && data.sections['cart-drawer']) {
      if (typeof data.item_count === 'number') drawer.classList.toggle('is-empty', data.item_count === 0);
      if (openDrawer || drawer.classList.contains('active')) drawer.renderContents(data);
      else {
        drawer.getSectionsToRender().forEach((section) => {
          const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          if (el && data.sections[section.id]) el.innerHTML = drawer.getSectionInnerHTML(data.sections[section.id], section.selector);
        });
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    }
    const id = cartPageSection();
    const html = id && data.sections[id];
    const next = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hx-cart]');
    if (next) {
      document.querySelector('[data-hx-cart]').replaceWith(next);
      init(next);
    }
  };

  // state: already read from a response that asked for hx-dgs, or undefined to fetch it.
  // Resolves to the correcting request's response (with the cart sections), or null.
  const syncDgs = (state) => {
    const read =
      state !== undefined
        ? Promise.resolve(state)
        : fetch(`${root()}?sections=${DGS_SECTION}`)
            .then((r) => r.json())
            .then((sections) => readDgs(sections[DGS_SECTION] || ''));
    return read
      .then((current) => {
        const req = dgsRequest(current);
        if (!req) return null;
        const drawer = getDrawer();
        const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
        if (cartPageSection()) sections.push(cartPageSection());
        return fetch(`${root()}${req.url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...req.body, sections, sections_url: window.location.pathname }),
        })
          .then((r) => r.json())
          .then((data) => (data.status ? null : data));
      })
      .catch(() => null);
  };

  // After a change made elsewhere (Dawn's drawer, page load): correct, re-render, recount
  const settleDgs = (state) =>
    syncDgs(state).then((fixed) => {
      if (!fixed) return;
      renderCart(fixed, false);
      refreshCount();
    });

  const addToCart = (items) => {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer) {
      body.sections = drawer.getSectionsToRender().map((section) => section.id).concat(DGS_SECTION);
      if (cartPageSection()) body.sections.push(cartPageSection());
      body.sections_url = window.location.pathname;
    }
    return fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) return { error: data.description || data.message };
        // Without the drawer the cart page corrects the surcharge on load
        const dgs = drawer ? syncDgs(readDgs((data.sections && data.sections[DGS_SECTION]) || '')) : Promise.resolve(null);
        return dgs.then((fixed) => {
          if (drawer) {
            drawer.classList.remove('is-empty');
            // Opens the drawer, and refreshes the cart page when adding from it
            renderCart(fixed && fixed.sections ? fixed : data, true);
          } else {
            window.location.href = `${root()}cart`;
          }
          publishCart(data);
          refreshCount();
          track('add_to_cart', { items });
          return { data };
        });
      });
  };

  if (cartEvents() && typeof subscribe === 'function') {
    subscribe(cartEvents().cartUpdate, (event) => {
      if (event && event.source === 'hatch') return;
      refreshCount();
      settleDgs();
    });
  }

  /* ---------------------------------------------------------------- */
  /* M2 delegation                                                     */
  /* ---------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;

    const megaTrigger = t.closest('[data-hx-mega-trigger]');
    if (megaTrigger) {
      const header = megaTrigger.closest('[data-hx-header]');
      const justHovered = Date.now() - megaHoverAt < 600;
      if (megaTrigger.getAttribute('aria-expanded') === 'true' && !justHovered) closeMega(header, false);
      else openMega(header, megaTrigger.getAttribute('aria-controls'));
      return;
    }
    const megaSwitch = t.closest('[data-hx-mega-switch]');
    if (megaSwitch) return openMega(megaSwitch.closest('[data-hx-header]'), megaSwitch.getAttribute('aria-controls'));

    document.querySelectorAll('[data-hx-header]').forEach((header) => {
      if (!header.contains(t)) closeMega(header, false);
    });

    const drawerOpen = t.closest('[data-hx-drawer-open]');
    if (drawerOpen) return openDrawer(drawerOpen);
    const drawerClose = t.closest('[data-hx-drawer-close]');
    if (drawerClose) return closeDrawer(drawerClose.closest('[data-hx-drawer]'));
    const drawerSub = t.closest('[data-hx-drawer-sub]');
    if (drawerSub) {
      const drawer = drawerSub.closest('[data-hx-drawer]');
      drawerLevel(drawer, drawerSub.dataset.hxDrawerSub);
      drawer.querySelector('[data-hx-drawer-back]').focus();
      return;
    }
    const drawerBack = t.closest('[data-hx-drawer-back]');
    if (drawerBack) {
      const drawer = drawerBack.closest('[data-hx-drawer]');
      const open = drawer.querySelector('[data-hx-drawer-level="2"]:not([hidden])');
      drawerLevel(drawer, null);
      const trigger = open && drawer.querySelector(`[data-hx-drawer-sub="${open.id}"]`);
      if (trigger) trigger.focus();
      return;
    }

    const cartLink = t.closest('[data-hx-cart-link]');
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartLink && cartDrawer && typeof cartDrawer.open === 'function') {
      event.preventDefault();
      cartDrawer.open(cartLink);
      return;
    }

    if (!t.closest('[data-hx-search]')) {
      document.querySelectorAll('[data-hx-search] input[type="search"]').forEach(closeSuggest);
    }
  });

  document.addEventListener('keydown', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const drawer = document.querySelector('[data-hx-drawer]:not([hidden])');
    if (drawer) {
      if (event.key === 'Escape') return closeDrawer(drawer);
      if (event.key === 'Tab') return trapFocus(event, drawer.querySelector('[role="dialog"]'));
    }
    if (t && t.matches('[data-hx-search] input[type="search"]')) {
      if (event.key === 'ArrowDown' && moveSuggest(t, 1)) return event.preventDefault();
      if (event.key === 'ArrowUp' && moveSuggest(t, -1)) return event.preventDefault();
      if (event.key === 'Escape') return closeSuggest(t);
      if (event.key === 'Enter') {
        const active = t.getAttribute('aria-activedescendant');
        const option = active && document.getElementById(active);
        if (option) {
          event.preventDefault();
          window.location.href = option.href;
        }
      }
      return;
    }
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-hx-header]').forEach((header) => {
        if (header.querySelector('[data-hx-mega-trigger][aria-expanded="true"]')) closeMega(header, true);
      });
    }
  });

  document.addEventListener('focusout', (event) => {
    const header = event.target instanceof Element && event.target.closest('[data-hx-header]');
    if (!header) return;
    const next = event.relatedTarget;
    if (next instanceof Element && header.contains(next)) return;
    if (next) closeMega(header, false);
  });


  /* ---------------------------------------------------------------- */
  /* M3: hero carousel                                                 */
  /* Tabs double as slide navigation (dots on phones). Autoplay only   */
  /* without reduced motion, pauses on hover, focus and the pause      */
  /* button. Slides are announced politely. Swipe on touch.            */
  /* ---------------------------------------------------------------- */

  const carousels = new WeakMap();

  const carouselState = (root) => {
    if (!carousels.has(root)) carousels.set(root, { index: 0, timer: 0, hover: false, focus: false, userPaused: false });
    return carousels.get(root);
  };

  const slidesOf = (root) => Array.from(root.querySelectorAll('[data-hx-slide]'));

  const autoplayMs = (root) => {
    const ms = Number(root.dataset.autoplay || 0);
    return ms && !reducedMotion() ? ms : 0;
  };

  const schedule = (root) => {
    const state = carouselState(root);
    clearTimeout(state.timer);
    const ms = autoplayMs(root);
    const playing = ms && !state.hover && !state.focus && !state.userPaused;
    root.toggleAttribute('data-playing', Boolean(playing));
    if (!playing) return;
    root.style.setProperty('--hx-slide-ms', `${ms}ms`);
    state.timer = setTimeout(() => goTo(root, state.index + 1, false), ms);
  };

  const goTo = (root, n, announce) => {
    const slides = slidesOf(root);
    if (!slides.length) return;
    const state = carouselState(root);
    state.index = (n + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      const active = i === state.index;
      slide.hidden = !active;
      slide.classList.toggle('is-in', active);
    });
    root.querySelectorAll('[data-hx-carousel-tab]').forEach((tab, i) => {
      const active = i === state.index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    // restart the progress bar
    root.removeAttribute('data-playing');
    void root.offsetWidth;
    if (announce) {
      const live = root.querySelector('[data-hx-carousel-live]');
      const title = slides[state.index].querySelector('.hx-h1');
      if (live) live.textContent = `${slides[state.index].getAttribute('aria-label')}: ${title ? title.textContent.trim() : ''}`;
    }
    schedule(root);
  };

  const initCarousels = (scope) => {
    scope.querySelectorAll('[data-hx-carousel]').forEach((root) => {
      if (root.dataset.hxReady) return;
      root.dataset.hxReady = '1';
      const state = carouselState(root);
      root.addEventListener('mouseenter', () => { state.hover = true; schedule(root); });
      root.addEventListener('mouseleave', () => { state.hover = false; schedule(root); });
      root.addEventListener('focusin', () => { state.focus = true; schedule(root); });
      root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) { state.focus = false; schedule(root); }
      });
      let startX = null;
      const track = root.querySelector('[data-hx-slides]');
      track.addEventListener('pointerdown', (event) => { if (event.pointerType !== 'mouse') startX = event.clientX; }, { passive: true });
      track.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) goTo(root, state.index + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
      schedule(root);
      // Later slides are lazy; fetch them once the page is in so a rotation never shows a blank frame
      const warm = () => root.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      if (document.readyState === 'complete') warm(); else window.addEventListener('load', warm, { once: true });
    });
  };

  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const root = t && t.closest('[data-hx-carousel]');
    if (!root) return;
    const state = carouselState(root);
    const step = t.closest('[data-hx-carousel-step]');
    if (step) return goTo(root, state.index + Number(step.dataset.hxCarouselStep), true);
    const tab = t.closest('[data-hx-carousel-tab]');
    if (tab) return goTo(root, Number(tab.dataset.hxCarouselTab), true);
    const pause = t.closest('[data-hx-carousel-pause]');
    if (pause) {
      state.userPaused = !state.userPaused;
      pause.setAttribute('aria-pressed', String(state.userPaused));
      pause.setAttribute('aria-label', state.userPaused ? pause.dataset.labelPlay : pause.dataset.labelPause);
      schedule(root);
    }
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target instanceof Element && event.target.closest('[data-hx-carousel-tab]');
    if (!tab) return;
    const root = tab.closest('[data-hx-carousel]');
    const state = carouselState(root);
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    goTo(root, state.index + keys[event.key], true);
    root.querySelectorAll('[data-hx-carousel-tab]')[carouselState(root).index].focus();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest && event.target.closest('[data-hx-slide]');
    const root = slide && slide.closest('[data-hx-carousel]');
    if (!root) return;
    carouselState(root).userPaused = true;
    goTo(root, slidesOf(root).indexOf(slide), false);
  });

  /* ---------------------------------------------------------------- */
  /* M3: add to cart from product cards (form[data-hx-add])            */
  /* ---------------------------------------------------------------- */

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-hx-add]')) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const card = form.closest('.hx-pcard, .hx-line, [data-hx-buybox]') || form.parentElement;
    const error = card.querySelector('.hx-pcard__error');
    const label = button.innerHTML;
    const data = new FormData(form);
    button.disabled = true;
    button.textContent = button.dataset.labelBusy || label;
    if (error) error.hidden = true;
    addToCart([{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }])
      .then((result) => {
        if (result.error) {
          if (error) { error.textContent = result.error; error.hidden = false; }
          button.innerHTML = label;
        } else {
          button.textContent = button.dataset.labelDone || '';
          setTimeout(() => { button.innerHTML = label; }, 1500);
        }
      })
      .catch(() => { button.innerHTML = label; })
      .finally(() => { button.disabled = false; });
  });


  /* ---------------------------------------------------------------- */
  /* M4: collection. Filters and sort re-render the section through    */
  /* the Section Rendering API and update the URL; the back button     */
  /* re-renders. Load more appends the next page. View is remembered.  */
  /* ---------------------------------------------------------------- */

  const store = {
    get: (key) => { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    set: (key, value) => { try { window.localStorage.setItem(key, value); } catch (e) { /* storage blocked */ } },
  };

  const applyView = (scope) => {
    const view = store.get('hx-view') === 'grid' ? 'grid' : 'list';
    scope.querySelectorAll('[data-hx-results]').forEach((list) => list.classList.toggle('is-grid', view === 'grid'));
    scope.querySelectorAll('[data-hx-view]').forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.hxView === view)));
  };

  const sectionUrl = (url, sectionId) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return u.toString();
  };

  let collectionController = null;

  const renderCollection = (url, push) => {
    const current = document.querySelector('[data-hx-collection]');
    if (!current) return;
    const sectionId = current.closest('.shopify-section').id.replace('shopify-section-', '');
    if (collectionController) collectionController.abort();
    collectionController = new AbortController();
    current.setAttribute('aria-busy', 'true');
    fetch(sectionUrl(url, sectionId), { signal: collectionController.signal })
      .then((r) => r.text())
      .then((html) => {
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hx-collection]');
        if (!next) return;
        const sheetOpen = !current.querySelector('[data-hx-sheet]').classList.contains('is-open') ? false : true;
        current.replaceWith(next);
        if (sheetOpen) next.querySelector('[data-hx-sheet]').classList.add('is-open');
        applyView(next);
        init(next);
        if (push) window.history.pushState({ hx: 'collection' }, '', url);
      })
      .catch(() => {})
      .finally(() => current.removeAttribute('aria-busy'));
  };

  // Show more / Show less on long filter lists
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-hx-facet-more]');
    if (!btn) return;
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    document.getElementById(btn.getAttribute('aria-controls')).classList.toggle('is-clipped', !open);
  });

  const facetsUrl = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => { if (String(value).trim() !== '') params.append(key, value); });
    return `${form.action}?${params.toString()}`;
  };

  const sheetMobile = () => !window.matchMedia('(min-width: 1024px)').matches;

  document.addEventListener('change', (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    const form = t.closest('[data-hx-facets]') || (t.matches('[data-hx-sort]') ? document.getElementById(t.getAttribute('form')) : null);
    if (!form) return;
    // phones and tablets apply from the sheet's button; sort always applies
    if (sheetMobile() && !t.matches('[data-hx-sort]')) return;
    renderCollection(facetsUrl(form), true);
    track('filter_applied', { url: facetsUrl(form) });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-hx-facets]')) return;
    event.preventDefault();
    renderCollection(facetsUrl(form), true);
    const sheet = form.closest('[data-hx-sheet]');
    if (sheet) closeSheet(sheet);
  });

  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-hx-collection]')) renderCollection(window.location.href, false);
  });

  let sheetReturn = null;
  const openSheet = (button) => {
    const sheet = document.getElementById(button.getAttribute('aria-controls'));
    if (!sheet) return;
    sheetReturn = button;
    sheet.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('hx-lock');
    sheet.querySelector('[role="dialog"]').focus();
  };
  const closeSheet = (sheet) => {
    sheet.classList.remove('is-open');
    document.documentElement.classList.remove('hx-lock');
    const button = sheetReturn || document.querySelector(`[aria-controls="${sheet.id}"]`);
    if (button) { button.setAttribute('aria-expanded', 'false'); button.focus(); }
    sheetReturn = null;
  };

  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const link = t.closest('[data-hx-facet-link]');
    if (link) {
      event.preventDefault();
      renderCollection(link.href, true);
      return;
    }
    const view = t.closest('[data-hx-view]');
    if (view) {
      store.set('hx-view', view.dataset.hxView);
      applyView(document);
      return;
    }
    const more = t.closest('[data-hx-more]');
    if (more) {
      event.preventDefault();
      const root = more.closest('[data-hx-collection]');
      const sectionId = root.closest('.shopify-section').id.replace('shopify-section-', '');
      more.setAttribute('aria-busy', 'true');
      fetch(sectionUrl(more.href, sectionId))
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = root.querySelector('[data-hx-results]');
          const firstNew = doc.querySelector('[data-hx-results] > .hx-line');
          doc.querySelectorAll('[data-hx-results] > .hx-line').forEach((item) => list.appendChild(item));
          const to = root.querySelector('[data-hx-to]');
          const nextTo = doc.querySelector('[data-hx-to]');
          if (to && nextTo) to.textContent = nextTo.textContent;
          ['[data-hx-more-wrap]', '.hx-coll__pages'].forEach((sel) => {
            const a = root.querySelector(sel);
            const b = doc.querySelector(sel);
            if (a && b) a.replaceWith(b);
          });
          init(root);
          const focusable = firstNew && list.querySelector(`[href="${firstNew.querySelector('a[href]').getAttribute('href')}"]:not([tabindex])`);
          if (focusable) focusable.focus();
        });
      return;
    }
    const sheetOpen = t.closest('[data-hx-sheet-open]');
    if (sheetOpen) return openSheet(sheetOpen);
    const sheetClose = t.closest('[data-hx-sheet-close]');
    if (sheetClose) return closeSheet(sheetClose.closest('[data-hx-sheet]'));
  });

  document.addEventListener('keydown', (event) => {
    const sheet = document.querySelector('[data-hx-sheet].is-open');
    if (!sheet) return;
    if (event.key === 'Escape') closeSheet(sheet);
    if (event.key === 'Tab') trapFocus(event, sheet.querySelector('[role="dialog"]'));
  });


  /* ---------------------------------------------------------------- */
  /* M4: product gallery. Thumbnails scroll the snap track; the track  */
  /* is swipeable below 768 with dots.                                 */
  /* ---------------------------------------------------------------- */

  const gallerySlides = (gallery) => Array.from(gallery.querySelectorAll('.hx-gallery__slide:not([hidden])'));

  const syncGallery = (gallery) => {
    const track = gallery.querySelector('[data-hx-gallery-track]');
    if (!track) return;
    const slide = gallerySlides(gallery)[Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))];
    const id = slide ? slide.dataset.mediaId : '';
    gallery.querySelectorAll('[data-hx-gallery-go]').forEach((thumb) => {
      if (thumb.dataset.hxGalleryGo === id) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
    });
    gallery.querySelectorAll('.hx-gallery__dot').forEach((dot) => dot.classList.toggle('is-on', dot.dataset.mediaId === id));
  };

  const galleryGo = (gallery, id, smooth) => {
    const track = gallery.querySelector('[data-hx-gallery-track]');
    const i = gallerySlides(gallery).findIndex((slide) => slide.dataset.mediaId === String(id));
    if (i >= 0) track.scrollTo({ left: track.clientWidth * i, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
  };

  // Of the variant images, only the chosen variant's shows, next to the shared ones
  const galleryVariant = (gallery, id) => {
    if (!gallery || !id) return;
    gallery.querySelectorAll('[data-variant-media]').forEach((el) => {
      el.hidden = el.dataset.mediaId !== String(id);
      if (!el.hidden) el.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    });
    galleryGo(gallery, id, false);
    syncGallery(gallery);
  };

  document.addEventListener('scroll', (event) => {
    const track = event.target;
    if (track instanceof Element && track.matches('[data-hx-gallery-track]')) {
      cancelAnimationFrame(track._hxFrame);
      track._hxFrame = requestAnimationFrame(() => syncGallery(track.closest('[data-hx-gallery]')));
    }
  }, { capture: true, passive: true });

  /* ---------------------------------------------------------------- */
  /* M4: variant picker. Resolves the variant from the pills, then     */
  /* re-renders price, unit price, SKU, stock and the buy form through */
  /* the Section Rendering API, and updates ?variant= in the URL.      */
  /* ---------------------------------------------------------------- */

  let variantController = null;

  const selectVariant = (picker) => {
    const root = picker.closest('[data-hx-product]');
    const variants = JSON.parse(picker.querySelector('[data-hx-variant-json]').textContent);
    const chosen = [];
    picker.querySelectorAll('[data-hx-option]:checked').forEach((input) => { chosen[Number(input.dataset.hxOption)] = input.value; });
    picker.querySelectorAll('[data-hx-option-label]').forEach((label) => { label.textContent = chosen[Number(label.dataset.hxOptionLabel)] || ''; });
    const variant = variants.find((v) => v.options.every((value, i) => value === chosen[i]));
    if (!variant) return;
    galleryVariant(root.querySelector('[data-hx-gallery]'), variant.media);
    const sectionId = root.closest('.shopify-section').id.replace('shopify-section-', '');
    const url = `${root.dataset.url}?variant=${variant.id}`;
    if (variantController) variantController.abort();
    variantController = new AbortController();
    root.setAttribute('aria-busy', 'true');
    fetch(`${url}&section_id=${sectionId}`, { signal: variantController.signal })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        root.querySelectorAll('[data-hx-swap]').forEach((el) => {
          const next = doc.querySelector(`[data-hx-swap="${el.dataset.hxSwap}"]`);
          if (next) el.replaceWith(next);
        });
        const stickyButton = root.querySelector('[data-hx-sticky-add]');
        if (stickyButton) stickyButton.disabled = !variant.available;
        window.history.replaceState({}, '', url);
        init(root);
      })
      .catch(() => {})
      .finally(() => root.removeAttribute('aria-busy'));
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.matches('[data-hx-option]')) selectVariant(input.closest('[data-hx-variants]'));
  });

  /* ---------------------------------------------------------------- */
  /* M4: sticky add to cart bar below lg, shown once the buy box has   */
  /* scrolled away.                                                    */
  /* ---------------------------------------------------------------- */

  const syncStickyBuy = () => {
    document.querySelectorAll('[data-hx-product]').forEach((root) => {
      const bar = root.querySelector('[data-hx-stickybuy]');
      const box = root.querySelector('[data-hx-buybox]');
      if (!bar || !box) return;
      bar.hidden = box.getBoundingClientRect().bottom > 0 || window.matchMedia('(min-width: 1024px)').matches;
    });
  };
  let stickyFrame = 0;
  window.addEventListener('scroll', () => {
    cancelAnimationFrame(stickyFrame);
    stickyFrame = requestAnimationFrame(syncStickyBuy);
  }, { passive: true });
  const initStickyBuy = () => syncStickyBuy();

  /* ---------------------------------------------------------------- */
  /* M4: recommendations (complementary and related), loaded after the */
  /* page so they never block it.                                      */
  /* ---------------------------------------------------------------- */

  const initRecs = (scope) => {
    scope.querySelectorAll('[data-hx-recs]').forEach((el) => {
      if (el.dataset.hxLoaded) return;
      el.dataset.hxLoaded = '1';
      fetch(el.dataset.url)
        .then((r) => r.text())
        .then((html) => {
          const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hx-recs]');
          if (next && next.innerHTML.trim()) {
            el.innerHTML = next.innerHTML;
            init(el);
          }
        })
        .catch(() => {});
    });
  };

  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const more = t.closest('[data-hx-clamp-toggle]');
    if (more) {
      const body = document.getElementById(more.getAttribute('aria-controls'));
      const open = body.classList.toggle('is-open');
      more.setAttribute('aria-expanded', String(open));
      const label = more.dataset.labelLess;
      more.dataset.labelLess = more.firstChild.textContent;
      more.firstChild.textContent = label;
      return;
    }
    const go = t.closest('[data-hx-gallery-go]');
    if (go) {
      galleryGo(go.closest('[data-hx-gallery]'), go.dataset.hxGalleryGo, true);
      return;
    }
    const sticky = t.closest('[data-hx-sticky-add]');
    if (sticky) {
      const form = sticky.closest('[data-hx-product]').querySelector('[data-hx-buybox] form[data-hx-add]');
      if (form) form.requestSubmit();
    }
  });

  // Customers also shop: the Liquid pool, shuffled once per session
  const sessionSeed = () => {
    try {
      let seed = sessionStorage.getItem('hx-seed');
      if (!seed) { seed = String(Math.floor(Math.random() * 1e9)); sessionStorage.setItem('hx-seed', seed); }
      return Number(seed);
    } catch (e) {
      return Math.floor(Math.random() * 1e9);
    }
  };

  const initShuffle = (scope) => {
    scope.querySelectorAll('[data-hx-shuffle]').forEach((list) => {
      if (list.dataset.hxShuffled) return;
      list.dataset.hxShuffled = '1';
      let seed = sessionSeed();
      const rand = () => { seed = (seed * 1664525 + 1013904223) % 4294967296; return seed / 4294967296; };
      const items = Array.from(list.children);
      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rand() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
      const limit = Number(list.dataset.hxShuffle) || items.length;
      items.forEach((item, i) => { item.hidden = i >= limit; list.appendChild(item); });
      if (list.id) syncScroller(list);
    });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-hx-add-all]')) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('.hx-pcard__error');
    const label = button.innerHTML;
    const ids = String(new FormData(form).get('ids') || '').split(',').filter(Boolean);
    button.disabled = true;
    button.textContent = button.dataset.labelBusy || '';
    addToCart(ids.map((id) => ({ id: Number(id), quantity: 1 })))
      .then((result) => {
        if (result.error && error) { error.textContent = result.error; error.hidden = false; }
      })
      .finally(() => { button.innerHTML = label; button.disabled = false; });
  });


  /* ---------------------------------------------------------------- */
  /* M5: angle viewer. Thumbnails, arrow keys and horizontal drag step */
  /* through the renders (phase one of the 3D viewer, spec 6.6).       */
  /* ---------------------------------------------------------------- */

  const showAngle = (viewer, n) => {
    const imgs = Array.from(viewer.querySelectorAll('[data-hx-angle-img]'));
    if (!imgs.length) return;
    const i = (n + imgs.length) % imgs.length;
    viewer.dataset.index = i;
    imgs.forEach((img, k) => { img.hidden = k !== i; if (k === i) img.loading = 'eager'; });
    viewer.querySelectorAll('[data-hx-angle]').forEach((t, k) => { if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    viewer.querySelectorAll('.hx-angles__dots > span').forEach((d, k) => d.classList.toggle('is-on', k === i));
    // A video slide stops when the viewer leaves it, and starts again (muted) on return when it autoplays
    imgs.forEach((slide, k) => {
      const video = slide.querySelector('[data-hx-video]');
      if (!video) return;
      clearTimeout(video.hxTimer);
      if (k !== i) stopVideo(video);
      else if (video.hasAttribute('data-hx-autoplay') && !reducedMotion()) {
        video.hxTimer = setTimeout(() => { if (Number(viewer.dataset.index) === k) playVideo(video, true); }, 350);
      }
    });
  };

  const initAngles = (scope) => {
    scope.querySelectorAll('[data-hx-angles]').forEach((viewer) => {
      if (viewer.dataset.hxReady) return;
      viewer.dataset.hxReady = '1';
      const stage = viewer.querySelector('[data-hx-angle-stage]');
      let startX = null;
      let startIndex = 0;
      let fromPoster = null;
      stage.addEventListener('pointerdown', (e) => {
        startX = e.clientX; startIndex = Number(viewer.dataset.index || 0);
        fromPoster = e.target instanceof Element ? e.target.closest('.hx-video__poster') : null;
        stage.setPointerCapture(e.pointerId);
      });
      stage.addEventListener('pointermove', (e) => {
        if (startX === null) return;
        const steps = Math.round((e.clientX - startX) / 60);
        showAngle(viewer, startIndex - steps);
      });
      // Pointer capture sends the click to the stage, so a tap on a video poster plays it here
      stage.addEventListener('pointerup', (e) => {
        if (fromPoster && startX !== null && Math.abs(e.clientX - startX) < 8 && fromPoster.isConnected) playVideo(fromPoster.closest('[data-hx-video]'));
        startX = null; fromPoster = null;
      });
      stage.addEventListener('pointercancel', () => { startX = null; fromPoster = null; });
      // A first slide that autoplays starts once the page has loaded, muted
      const auto = viewer.querySelector('[data-hx-angle-img]:not([hidden]) [data-hx-video][data-hx-autoplay]');
      if (auto && !reducedMotion()) {
        const start = () => setTimeout(() => { if (Number(viewer.dataset.index || 0) === 0) playVideo(auto, true); }, 300);
        if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true });
      }
      stage.addEventListener('keydown', (e) => {
        const dir = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!dir) return;
        e.preventDefault();
        showAngle(viewer, Number(viewer.dataset.index || 0) + dir);
      });
    });
  };

  /* ---------------------------------------------------------------- */
  /* M5: lightbox on <dialog>, opened at the clicked angle             */
  /* ---------------------------------------------------------------- */

  let lightboxReturn = null;

  /* ---------------------------------------------------------------- */
  /* M5: YouTube click-to-load. Nothing from YouTube loads but the     */
  /* poster until the visitor asks for the player.                     */
  /* ---------------------------------------------------------------- */

  // maxresdefault does not exist for every video; YouTube then serves a 120px placeholder
  const ytFallback = (img) => {
    if (img.dataset.hxYt && img.complete && img.naturalWidth && img.naturalWidth < 200) {
      img.src = img.dataset.hxYt;
      delete img.dataset.hxYt;
    }
  };
  document.addEventListener('load', (event) => { if (event.target instanceof HTMLImageElement) ytFallback(event.target); }, true);

  // quiet: started by the page, not the visitor, so muted (browsers only allow that) and looping;
  // without data-hx-controls it also runs bare: no YouTube controls and no pointer events
  const playVideo = (wrap, quiet = false) => {
    const poster = wrap && wrap.querySelector('.hx-video__poster');
    if (!poster) return;
    const id = encodeURIComponent(wrap.dataset.hxVideo);
    const bare = quiet && !wrap.hasAttribute('data-hx-controls');
    const frame = document.createElement('iframe');
    frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`
      + (quiet ? `&mute=1&loop=1&playlist=${id}` : '')
      + (bare ? '&controls=0&disablekb=1&fs=0&iv_load_policy=3' : '');
    frame.title = wrap.dataset.title || 'Video';
    frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
    frame.allowFullscreen = true;
    frame.className = bare ? 'hx-video__frame hx-video__frame--bare' : 'hx-video__frame';
    if (bare) frame.tabIndex = -1;
    wrap.hxPoster = poster;
    poster.replaceWith(frame);
    wrap.classList.add('is-playing');
    if (!quiet) frame.focus();
  };

  // Back to the poster, which stops the player
  const stopVideo = (wrap) => {
    const frame = wrap.querySelector('iframe');
    if (!frame || !wrap.hxPoster) return;
    frame.replaceWith(wrap.hxPoster);
    wrap.classList.remove('is-playing');
  };

  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const angle = t.closest('[data-hx-angle]');
    if (angle) return showAngle(angle.closest('[data-hx-angles]'), Number(angle.dataset.hxAngle));
    const open = t.closest('[data-hx-lightbox]');
    if (open) {
      const dialog = open.closest('section').querySelector('[data-hx-lightbox-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      lightboxReturn = open;
      dialog.showModal();
      const track = dialog.querySelector('[data-hx-lightbox-track]');
      track.scrollTo({ left: track.clientWidth * Number(open.dataset.hxLightbox) });
      return;
    }
    if (t.closest('[data-hx-lightbox-close]') || t.matches('[data-hx-lightbox-dialog]')) {
      const dialog = t.closest('[data-hx-lightbox-dialog]');
      dialog.close();
      if (lightboxReturn) lightboxReturn.focus();
      return;
    }
    const poster = t.closest('.hx-video__poster');
    if (poster) playVideo(poster.closest('[data-hx-video]'));
  });

  /* ---------------------------------------------------------------- */
  /* M5: sub-nav scroll-spy                                            */
  /* ---------------------------------------------------------------- */

  const pruneSubnav = (scope) => {
    scope.querySelectorAll('[data-hx-subnav] a[href^="#"]').forEach((link) => {
      if (!document.getElementById(link.getAttribute('href').slice(1))) link.parentElement.hidden = true;
    });
  };

  const syncSubnav = () => {
    document.querySelectorAll('[data-hx-subnav]').forEach((nav) => {
      const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
      let current = links[0];
      links.forEach((link) => {
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (target && target.getBoundingClientRect().top < 200) current = link;
      });
      links.forEach((link) => { if (link === current) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current'); });
      if (current && nav.dataset.current !== current.getAttribute('href')) {
        nav.dataset.current = current.getAttribute('href');
        reveal(current.closest('ul') || nav, current);
      }
    });
  };
  let subnavFrame = 0;
  window.addEventListener('scroll', () => {
    cancelAnimationFrame(subnavFrame);
    subnavFrame = requestAnimationFrame(syncSubnav);
  }, { passive: true });


  /* ---------------------------------------------------------------- */
  /* M6: document library, filtered client side by type and search    */
  /* ---------------------------------------------------------------- */

  const filterDocs = (lib) => {
    const term = (lib.querySelector('[data-hx-doc-search]').value || '').trim().toLowerCase();
    const active = lib.querySelector('[data-hx-doc-type][aria-pressed="true"]');
    const type = active ? active.dataset.hxDocType : '';
    let shown = 0;
    lib.querySelectorAll('[data-hx-doc]').forEach((row) => {
      const ok = (!type || row.dataset.type === type) && (!term || row.dataset.text.includes(term));
      row.hidden = !ok;
      if (ok) shown += 1;
    });
    lib.querySelectorAll('[data-hx-doc-group]').forEach((group) => { group.hidden = !group.querySelector('[data-hx-doc]:not([hidden])'); });
    const none = lib.querySelector('[data-hx-doc-none]');
    if (none) none.hidden = shown > 0;
  };

  document.addEventListener('input', (event) => {
    const t = event.target;
    if (t instanceof Element && t.matches('[data-hx-doc-search]')) filterDocs(t.closest('[data-hx-doclib]'));
  });

  document.addEventListener('click', (event) => {
    const chip = event.target instanceof Element && event.target.closest('[data-hx-doc-type]');
    if (!chip) return;
    const lib = chip.closest('[data-hx-doclib]');
    lib.querySelectorAll('[data-hx-doc-type]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    filterDocs(lib);
  });

  // Side navs become a select below lg; choosing an option navigates
  document.addEventListener('change', (event) => {
    const t = event.target;
    if (t instanceof HTMLSelectElement && t.matches('[data-hx-nav-select]') && t.value) window.location.href = t.value;
  });


  /* ---------------------------------------------------------------- */
  /* M7: cart. Quantity changes and removes go through /cart/change.js */
  /* and re-render the cart section (items and summary together).      */
  /* ---------------------------------------------------------------- */

  let cartTimer = 0;

  const changeLine = (cart, line, quantity) => {
    const sectionId = cart.closest('.shopify-section').id.replace('shopify-section-', '');
    cart.setAttribute('aria-busy', 'true');
    if (quantity === 0) track('remove_from_cart', { line });
    fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity, sections: [sectionId, DGS_SECTION], sections_url: window.location.pathname }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) {
          const error = cart.querySelector('.hx-cart__error');
          if (error) { error.textContent = data.description || data.message; error.hidden = false; }
          return null;
        }
        return syncDgs(readDgs((data.sections && data.sections[DGS_SECTION]) || '')).then((fixed) => {
          const final = fixed && fixed.sections ? fixed : data;
          const html = final.sections && final.sections[sectionId];
          const next = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hx-cart]');
          if (next) { cart.replaceWith(next); init(next); }
          if (typeof final.item_count === 'number') setCount(final.item_count);
          else refreshCount();
          publishCart(final);
        });
      })
      .catch(() => {})
      .finally(() => cart.removeAttribute('aria-busy'));
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-hx-cart-qty]')) return;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(() => changeLine(input.closest('[data-hx-cart]'), Number(input.dataset.hxCartQty), Number(input.value)), 350);
  });

  document.addEventListener('click', (event) => {
    const remove = event.target instanceof Element && event.target.closest('[data-hx-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    changeLine(remove.closest('[data-hx-cart]'), Number(remove.dataset.hxCartRemove), 0);
  });

  /* ---------------------------------------------------------------- */
  /* M7: article contents, built from the h2s; a "Jump to" select     */
  /* below lg                                                          */
  /* ---------------------------------------------------------------- */

  const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

  const initToc = (scope) => {
    scope.querySelectorAll('[data-hx-toc]').forEach((toc) => {
      if (toc.dataset.hxReady) return;
      toc.dataset.hxReady = '1';
      const source = toc.parentElement.querySelector('[data-hx-toc-source]');
      const heads = source ? Array.from(source.querySelectorAll('h2')) : [];
      if (heads.length < 2) return;
      const list = toc.querySelector('[data-hx-toc-list]');
      const select = toc.querySelector('[data-hx-toc-select]');
      heads.forEach((h, i) => {
        if (!h.id) h.id = slugify(h.textContent) || `section-${i + 1}`;
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = h.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
        const option = document.createElement('option');
        option.value = `#${h.id}`;
        option.textContent = a.textContent;
        select.appendChild(option);
      });
      toc.hidden = false;
    });
  };

  document.addEventListener('change', (event) => {
    const t = event.target;
    if (t instanceof HTMLSelectElement && t.matches('[data-hx-toc-select]') && t.value) {
      const target = document.querySelector(t.value);
      if (target) { target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth' }); target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); }
    }
  });

  /* ---------------------------------------------------------------- */
  /* Quote popup (hx-quote). The form posts with fetch and is replaced */
  /* by the returned page's copy; a challenge falls back to a post.    */
  /* ---------------------------------------------------------------- */

  let quoteReturn = null;

  // Powerful Form Builder form (data-hx-quote-pfb): the message starts with the product, unless
  // the visitor has already written their own. The app restores a draft of the fields on every
  // page, so a message that is still the line filled in last time (another product) is replaced.
  const prefillQuoteApp = (box, item) => {
    const area = box && box.querySelector('textarea');
    if (!area || !item || !box.dataset.prefill) return;
    let last = '';
    try { last = window.localStorage.getItem('hxQuotePrefill') || ''; } catch (e) { /* storage blocked */ }
    if (area.value && area.value !== last) return;
    area.value = box.dataset.prefill.replace('[item]', item);
    try { window.localStorage.setItem('hxQuotePrefill', area.value); } catch (e) { /* storage blocked */ }
    area.dispatchEvent(new Event('input', { bubbles: true }));
    area.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const openQuote = (dialog, from) => {
    const ctx = (from && from.closest('[data-hx-quote-for]')) || document.querySelector('main [data-hx-quote-for]');
    const item = ctx && ctx.dataset.hxQuoteFor;
    const field = dialog.querySelector('[data-hx-quote-item]');
    if (field && item) field.value = item;
    prefillQuoteApp(dialog.querySelector('[data-hx-quote-pfb]'), item);
    quoteReturn = from;
    dialog.showModal();
    const first = dialog.querySelector('[data-hx-quote-done], [data-hx-quote-error], input:not([type="hidden"])');
    if (first) first.focus();
  };

  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const dialog = document.querySelector('[data-hx-quote]');
    if (!t || !dialog || typeof dialog.showModal !== 'function') return;
    if (t === dialog || t.closest('[data-hx-quote-close]')) {
      dialog.close();
      if (quoteReturn) quoteReturn.focus();
      return;
    }
    const a = t.closest('a[href]');
    if (!a || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const url = new URL(a.href, window.location.href);
    const paths = (dialog.dataset.paths || '').split(/\s+/).filter(Boolean);
    if (url.origin !== window.location.origin || !paths.includes(url.pathname)) return;
    event.preventDefault();
    openQuote(dialog, a);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-hx-quote-form]')) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button) { button.disabled = true; button.textContent = button.dataset.labelBusy || ''; }
    fetch(form.action, { method: 'POST', body: new FormData(form) })
      .then((r) => {
        if (/\/challenge/.test(r.url)) throw new Error('challenge');
        return r.text();
      })
      .then((html) => {
        const next = new DOMParser().parseFromString(html, 'text/html').getElementById(form.id);
        if (!next) throw new Error('missing');
        form.innerHTML = next.innerHTML;
        const focus = form.querySelector('[data-hx-quote-done], [data-hx-quote-error]');
        if (focus) focus.focus();
        if (form.querySelector('[data-hx-quote-done]')) track('quote_requested', { item: new FormData(form).get('contact[Product]') || '' });
      })
      .catch(() => HTMLFormElement.prototype.submit.call(form));
  });

  /* Contact and quote forms delivered by FormSubmit (data-hx-lead holds the AJAX address). */
  /* The answer is JSON; the form shows its sent or failed message in place.             */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.dataset.hxLead) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const label = button ? button.innerHTML : '';
    const done = form.querySelector('[data-hx-lead-done]');
    const failed = form.querySelector('[data-hx-lead-error]');
    const fields = form.querySelector('[data-hx-lead-fields]');
    const data = new FormData(form);
    if (data.get('_honey')) return;
    data.append('Page', window.location.href);
    if (failed) failed.hidden = true;
    if (button) { button.disabled = true; button.textContent = button.dataset.labelBusy || ''; }
    fetch(form.dataset.hxLead, { method: 'POST', body: data, headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((res) => {
        if (String(res.success) !== 'true') throw new Error(res.message || 'not sent');
        if (fields) fields.hidden = true;
        if (done) { done.hidden = false; done.focus(); }
        track(data.get('Product') !== null ? 'quote_requested' : 'contact_submitted', { form: data.get('Form') || '', item: data.get('Product') || '' });
      })
      .catch(() => {
        if (failed) { failed.hidden = false; failed.focus(); }
        if (button) { button.disabled = false; button.innerHTML = label; }
      });
  });

  // After a plain post (no JavaScript fetch), reopen the popup on its result
  const initQuote = () => {
    const dialog = document.querySelector('[data-hx-quote]');
    if (dialog && !dialog.open && typeof dialog.showModal === 'function' && dialog.querySelector('[data-hx-quote-done], [data-hx-quote-error]')
      && /contact_posted|HxQuoteForm/.test(window.location.href)) openQuote(dialog, null);
  };

  /* ---------------------------------------------------------------- */
  /* M8: analytics events (spec 13), published to Shopify's customer  */
  /* events layer so pixels can subscribe. Nothing is sent from here.  */
  /* ---------------------------------------------------------------- */

  const track = (name, data = {}) => {
    try { if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(name, data); } catch (e) { /* never break the page */ }
  };

  document.addEventListener('click', (event) => {
    const a = event.target instanceof Element && event.target.closest('a, button');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const text = (a.textContent || '').toLowerCase();
    if (a.matches('[name="checkout"], #CartDrawer-Checkout')) track('begin_checkout');
    else if (href.includes('quote')) track('quote_requested', { url: window.location.pathname });
    else if (text.includes('demo') && href) track('demo_requested', { url: window.location.pathname });
    else if (a.closest('.hx-docs, .hx-mspecs__docs, [data-hx-doc]')) track('spec_sheet_downloaded', { title: text.trim().slice(0, 120), url: href });
    else if (a.closest('.hx-video__poster')) track('video_played', { id: a.closest('[data-hx-video]').dataset.hxVideo });
  }, { capture: true });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.matches('[role="search"]')) track('search', { terms: (new FormData(form).get('q') || '').toString() });
  }, { capture: true });

  /* ---------------------------------------------------------------- */
  /* Initial state, and again when the editor re-renders a section    */
  /* ---------------------------------------------------------------- */

  // The sticky sub-nav sits under the sticky header, whose height changes by breakpoint
  const measureHeader = () => {
    const header = document.querySelector('.hx-header-section');
    if (header) document.documentElement.style.setProperty('--hx-header-h', `${header.offsetHeight}px`);
  };

  const init = (root = document) => {
    measureHeader();
    if (root === document && document.querySelector('[data-hx-product]')) track('view_item', { url: window.location.pathname });
    root.querySelectorAll('[data-hx-qty]').forEach(syncQty);
    root.querySelectorAll('.hx-scroller[id]').forEach(syncScroller);
    root.querySelectorAll('[data-hx-snap]').forEach(syncSnap);
    initCarousels(root);
    applyView(root);
    initStickyBuy(root);
    initRecs(root);
    initShuffle(root);
    root.querySelectorAll('[data-hx-clamp]:not(.is-open)').forEach((el) => {
      const short = el.scrollHeight <= el.clientHeight + 8;
      el.classList.toggle('is-short', short);
      const btn = document.querySelector(`[aria-controls="${el.id}"][data-hx-clamp-toggle]`);
      if (btn && el.clientHeight) btn.hidden = short;
    });
    root.querySelectorAll('img[data-hx-yt]').forEach(ytFallback);
    initAngles(root);
    initToc(root);
    pruneSubnav(root);
  };

  init();
  initQuote();
  // Page load: correct a cart changed where this script did not run (cart links, reorders,
  // no-JS forms), once the drawer is defined so it can be re-rendered too
  const drawerDefined =
    document.querySelector('cart-drawer') && window.customElements
      ? Promise.race([customElements.whenDefined('cart-drawer'), new Promise((resolve) => setTimeout(resolve, 3000))])
      : Promise.resolve();
  drawerDefined.then(() => settleDgs(readDgs(document)));
  let resizeFrame = 0;
  window.addEventListener(
    'resize',
    () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => init());
    },
    { passive: true }
  );
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.Hatch = { init, addToCart, refreshCount, money, root, escapeHtml, syncQty, selectTab, track };
})();
